import { Autocomplete, CircularProgress, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../constants/axiosInstance';

interface CellRendererProps {
  value: unknown;
  onChange: (newValue: unknown) => void;
  meta?: Record<string, unknown>;
  usedSensorIds: Set<string>;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const ReadOnlyCellRenderer: React.FC<CellRendererProps> = ({ value }) => (
  <Typography variant="body2" sx={{ p: '8.5px 14px', minHeight: '39.91px', boxSizing: 'border-box' }}>
    {String(value || '')}
  </Typography>
);

export const TextCellRenderer: React.FC<CellRendererProps> = ({ value, onChange }) => (
  <TextField value={value || ''} onChange={(e) => onChange(e.target.value)} variant="outlined" size="small" fullWidth multiline />
);

export const AsyncAutocompleteRenderer: React.FC<CellRendererProps> = ({ value, onChange, meta, usedSensorIds }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { plantId } = useParams();
  const debouncedValue = useDebounce(inputValue, 400);
  const endpoint = meta?.endpoint as string;

  const { data: options = [], isLoading } = useQuery({
    queryKey: ['diSensors', endpoint, debouncedValue, plantId],
    queryFn: async () => {
      const { data } = await axiosInstance().get(`${endpoint}?sensorTag=${debouncedValue}&assetId=${plantId}`);
      return data.data || [];
    },
    enabled: open && !!endpoint,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      options={options}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      filterOptions={(x) => x}
      getOptionDisabled={(option: any) => {
        const currentValue = value as any;
        const currentSensorId = typeof currentValue === 'object' ? currentValue?.sensorId : null;
        return option.sensorId !== currentSensorId && usedSensorIds.has(option.sensorId);
      }}
      getOptionLabel={(option: any) => option.sensor || ''}
      isOptionEqualToValue={(option, value) => option.sensorId === value.sensorId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={meta?.label as string || "DI Sensor"}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
      size="small"
    />
  );
};